import type { Vulnerability } from '@/api/client';
import { cn } from '@/lib/utils';

const statusColor: Record<string, string> = {
  Open:       'text-bad',
  InProgress: 'text-warn',
  Mitigated:  'text-good',
  Resolved:   'text-good',
  Accepted:   'text-accent-cyan',
};

export function VulnerabilitySummary({ items, compact = false }: { items: Vulnerability[]; compact?: boolean }) {
  const severities: Vulnerability['severity'][] = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
  const summary = severities.map((s) => {
    const rows = items.filter((i) => i.severity === s);
    const assets = new Set(rows.map((r) => r.assetName)).size;
    const status = rows.find((r) => r.status === 'Open')?.status
      ?? rows.find((r) => r.status === 'InProgress')?.status
      ?? rows[0]?.status
      ?? 'Resolved';
    return { severity: s, finding: rows.length, asset: assets, status };
  });

  return (
    <div className={compact ? 'rounded-md border border-[#19395a] bg-[#071d31] overflow-hidden' : 'card xl:col-span-2'}>
      <div className="px-2.5 py-2">
        <div className="text-[12px] font-semibold text-[#dbe7f3]">Vulnerability Summary</div>
      </div>
      <table className="w-full text-[10px]">
        <thead className="text-[#d7e4f1] bg-[#112a43] border-y border-[#1a4368]">
          <tr>
            <th className="text-left font-medium py-1.5 pl-2">Severity</th>
            <th className="text-center font-medium py-1.5">Finding</th>
            <th className="text-center font-medium py-1.5">Asset</th>
            <th className="text-left font-medium py-1.5 pr-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((v) => (
            <tr key={v.severity} className="border-b border-[#15375b]/75 last:border-0 hover:bg-[#0d2740]">
              <td className="py-1.5 pl-2">
                <span className={cn('inline-flex min-w-[50px] justify-center rounded px-1.5 py-0.5 text-[9px] font-semibold shadow-sm', `severity-${v.severity}`)}>{v.severity}</span>
              </td>
              <td className="py-1.5 text-center text-[#dbe7f3]">{v.finding}</td>
              <td className="py-1.5 text-center text-[#dbe7f3]">{v.asset}</td>
              <td className="py-1.5 pr-2">
                <span className={cn('inline-flex items-center gap-1 font-medium', statusColor[v.status] || 'text-accent-cyan')}>
                  <span className="grid h-3 w-3 place-items-center rounded-full border border-current text-[8px] leading-none">{v.status === 'InProgress' ? '!' : '✓'}</span>
                  {v.status === 'InProgress' ? 'In Progress' : v.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
